import React, { useState, useEffect } from 'react';
import { Promotion, PromotionRequest } from '../../types/promotion.types';
import { ServiceType } from '../../types/barber.types';
import { promotionService } from '../../services/promotionService';
import { serviceTypeService } from '../../services/serviceTypeService';
import { useToast } from '../../context/ToastContext';
import AdminHeader from '../../components/admin/AdminHeader';

const PromotionManagement: React.FC = () => {
    const { showToast } = useToast();
    const [promotions, setPromotions] = useState<Promotion[]>([]);
    const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    
    // Form state
    const [currentId, setCurrentId] = useState<number | null>(null);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [price, setPrice] = useState<number>(0);
    const [isActive, setIsActive] = useState(true);
    const [serviceTypeId, setServiceTypeId] = useState<number>(0);
    const [multiplier, setMultiplier] = useState<number>(2);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [promosData, servicesData] = await Promise.all([
                promotionService.getAllPromotions(),
                serviceTypeService.getAllServices()
            ]);
            setPromotions(promosData);
            setServiceTypes(servicesData);
            if (servicesData.length > 0) {
                setServiceTypeId(servicesData[0].id!);
            }
        } catch (error) {
            console.error('Error loading data:', error);
            showToast('Error al cargar datos', 'error');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setIsEditing(false);
        setCurrentId(null);
        setName('');
        setDescription('');
        setPrice(0);
        setIsActive(true);
        setMultiplier(2);
        if (serviceTypes.length > 0) {
            setServiceTypeId(serviceTypes[0].id!);
        }
    };

    const handleEdit = (promo: Promotion) => {
        setIsEditing(true);
        setCurrentId(promo.id);
        setName(promo.name);
        setDescription(promo.description || '');
        setPrice(promo.price);
        setIsActive(promo.isActive);
        setServiceTypeId(promo.serviceTypeId);
        setMultiplier(promo.multiplier);
    };

    const handleDelete = async (id: number) => {
        if (window.confirm('¿Estás seguro de eliminar esta promoción?')) {
            try {
                await promotionService.deletePromotion(id);
                showToast('Promoción eliminada exitosamente', 'success');
                loadData();
            } catch (error) {
                console.error('Error deleting promotion:', error);
                showToast('Error al eliminar la promoción', 'error');
            }
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const request: PromotionRequest = {
                name,
                description,
                price,
                isActive,
                serviceTypeId,
                multiplier
            };

            if (isEditing && currentId) {
                await promotionService.updatePromotion(currentId, request);
                showToast('Promoción actualizada exitosamente', 'success');
            } else {
                await promotionService.createPromotion(request);
                showToast('Promoción creada exitosamente', 'success');
            }
            
            resetForm();
            loadData();
        } catch (error) {
            console.error('Error saving promotion:', error);
            showToast('Error al guardar la promoción', 'error');
        }
    };

    if (loading) {
        return <div className="p-xl text-center"><div className="spinner"></div></div>;
    }

    return (
        <>
            <AdminHeader title="🎁 Gestión de Promociones" />
            <div className="container fade-in">

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-lg">
                <div className="lg:col-span-1">
                    <div className="card p-md sticky" style={{ top: '2rem' }}>
                        <h2 className="mb-md">{isEditing ? 'Editar Promoción' : 'Nueva Promoción'}</h2>
                        <form onSubmit={handleSubmit} className="flex flex-col gap-md">
                            <div>
                                <label className="form-label">Nombre de la Promoción</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={name}
                                    onChange={e => setName(e.target.value)}
                                    placeholder="Ej: Promo 2 Cortes"
                                    required
                                />
                            </div>

                            <div>
                                <label className="form-label">Descripción</label>
                                <textarea
                                    className="form-textarea"
                                    value={description}
                                    onChange={e => setDescription(e.target.value)}
                                    placeholder="Detalles (Opcional)"
                                    rows={3}
                                />
                            </div>

                            <div>
                                <label className="form-label">Precio Total ($)</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    value={price}
                                    onChange={e => setPrice(Number(e.target.value))}
                                    min="0"
                                    required
                                />
                            </div>

                            <div>
                                <label className="form-label">Servicio Base</label>
                                <select 
                                    className="form-input" 
                                    value={serviceTypeId} 
                                    onChange={e => setServiceTypeId(Number(e.target.value))}
                                    required
                                >
                                    <option value={0} disabled>Seleccione un servicio</option>
                                    {serviceTypes.map(st => (
                                        <option key={st.id} value={st.id}>{st.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="form-label">Multiplicador (Cantidad de turnos)</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    value={multiplier}
                                    onChange={e => setMultiplier(Number(e.target.value))}
                                    min="2"
                                    max="5"
                                    required
                                />
                                <small className="text-secondary block mt-xs">
                                    Ej: 2 significa que el sistema buscará 2 turnos consecutivos del servicio seleccionado.
                                </small>
                            </div>

                            <div className="flex items-center gap-sm mt-sm">
                                <input
                                    type="checkbox"
                                    id="isActive"
                                    checked={isActive}
                                    onChange={e => setIsActive(e.target.checked)}
                                    style={{ width: '1.2rem', height: '1.2rem' }}
                                />
                                <label htmlFor="isActive" className="m-0 font-bold cursor-pointer">Promoción Activa</label>
                            </div>

                            <div className="flex gap-sm mt-md">
                                <button type="submit" className="btn btn-primary flex-1">
                                    {isEditing ? 'Actualizar' : 'Crear'}
                                </button>
                                {isEditing && (
                                    <button type="button" className="btn btn-outline flex-1" onClick={resetForm}>
                                        Cancelar
                                    </button>
                                )}
                            </div>
                        </form>
                    </div>
                </div>

                <div className="lg:col-span-2">
                    <div className="card p-md">
                        <h2 className="mb-md">Promociones Existentes</h2>
                        {promotions.length === 0 ? (
                            <p className="text-secondary italic text-center p-lg bg-gray-50 rounded-lg">
                                No hay promociones creadas.
                            </p>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-md">
                                {promotions.map(promo => (
                                    <div key={promo.id} className={`card border-2 p-sm ${!promo.isActive ? 'opacity-70 border-gray-200' : 'border-accent border-opacity-30'}`}>
                                        <div className="flex justify-between items-start mb-sm">
                                            <h3 className="m-0 text-lg">{promo.name}</h3>
                                            <span className={`badge ${promo.isActive ? 'badge-success' : 'badge-secondary'}`}>
                                                {promo.isActive ? 'Activa' : 'Inactiva'}
                                            </span>
                                        </div>
                                        <p className="font-bold text-accent text-xl mb-xs">${promo.price}</p>
                                        <p className="text-sm text-secondary mb-sm">{promo.serviceTypeName} (x{promo.multiplier})</p>
                                        {promo.description && (
                                            <p className="text-sm bg-gray-50 p-xs rounded mb-md">{promo.description}</p>
                                        )}
                                        <div className="flex gap-xs mt-auto pt-sm border-t">
                                            <button className="btn btn-sm btn-outline flex-1" onClick={() => handleEdit(promo)}>
                                                Editar
                                            </button>
                                            <button className="btn btn-sm btn-outline text-error border-error border-opacity-30 flex-1 hover:bg-error hover:text-white" onClick={() => handleDelete(promo.id)}>
                                                Eliminar
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            </div>
        </>
    );
};

export default PromotionManagement;
